import { Chip, Grid, Stack } from '@mui/material'
import { CheckType } from '@planning/app/api'
import { useTranslate } from '@tolgee/react'
import { observer } from 'mobx-react-lite'
import { FC, useMemo } from 'react'
import { IInspectionRailCar, RailTallyViewStore } from '../../Stores/RailTallyViewStore'
import { RailCarCard } from '../RailCarCard'
import { sortRailcars } from './sortRailcars'

interface IProps {
  railcars: IInspectionRailCar[]
  searchHighlight?: string
  operation?: CheckType
  store: RailTallyViewStore
  confirmedRailcarIds: number[]
  onSelectRailcar: (railcarId?: number) => void
  onDeleteRailcar?: (railcar: IInspectionRailCar) => void
  onReportRailcarIssue?: (railcar: IInspectionRailCar) => void
}

interface IRailcarListGridProps extends IProps {
  isPendingInspection: boolean
}

const RailcarListGrid = ({
  railcars,
  operation,
  store,
  searchHighlight,
  confirmedRailcarIds,
  onSelectRailcar,
  onDeleteRailcar,
  isPendingInspection,
  onReportRailcarIssue,
}: IRailcarListGridProps) => {
  // We should move logic like this into a store
  const filteredRailcars = useMemo(() => {
    let filteredList = railcars.filter(railcar => {
      if (operation === CheckType.DischargePreparation) {
        return isPendingInspection ? !railcar.dischargeCheck : railcar.dischargeCheck
      }

      if (operation === CheckType.LoadPreparation) {
        return isPendingInspection ? !railcar.loadCheck : railcar.loadCheck
      }
      if (operation === CheckType.DepartureCheck) {
        return isPendingInspection ? !railcar.departureCheck : railcar.departureCheck
      }

      return !railcar.dischargeCheck && !railcar.loadCheck && !railcar.departureCheck
    })

    if (isPendingInspection) {
      filteredList = filteredList.filter(railcar => {
        if (confirmedRailcarIds.includes(railcar.railcarId)) return false

        if (
          store &&
          store.selectedRailTrackPosition &&
          store.selectedRailTrackPosition.data.checkinDate !== null
        ) {
          return railcar.railcarId !== store.selectedRailTrackPosition.data.railcarId
        }

        return railcar
      })
    }

    return filteredList
  }, [railcars, operation, isPendingInspection, confirmedRailcarIds, store])

  return (
    <Grid container marginTop={1.5} flexDirection={'column'} gap={1.5}>
      {filteredRailcars.sort(sortRailcars).map(railcar => (
        <Grid key={`railcar-${railcar.name}`}>
          <RailCarCard
            sequence={railcar.sequence}
            containers={operation !== CheckType.LoadPreparation ? railcar.containers : []}
            operation={operation}
            railcar={railcar.name}
            isMissingRailcar={railcar.isMissingRailcar}
            isDischargeChecked={!!railcar.dischargeCheck}
            isLoadPrepChecked={!!railcar.loadCheck}
            isDepartureChecked={!!railcar.departureCheck}
            searchHighlight={searchHighlight}
            onSelectRailcar={() => {
              onSelectRailcar(railcar.railcarId)
            }}
            onDelete={onDeleteRailcar ? () => onDeleteRailcar(railcar) : undefined}
            onReportRailcarIssue={
              onReportRailcarIssue ? () => onReportRailcarIssue(railcar) : undefined
            }
          />
        </Grid>
      ))}
    </Grid>
  )
}

// [RailTally] TODO: RailTally shows Load- and Discharge containers
export const InspectRailcarList: FC<IProps> = observer((props: IProps) => {
  const { t } = useTranslate()

  const showInspectedRailcarsChip = useMemo(
    () => props.operation === CheckType.DischargePreparation || CheckType.DepartureCheck,
    [props.operation],
  )

  return (
    <Stack>
      <Stack flexDirection='row'>
        {showInspectedRailcarsChip && (
          <Chip
            label={t('showInspectedRailcars', 'Show inspected railcars')}
            color={props.store.toggleShowInspectedRailcar ? 'primary' : undefined}
            onClick={() =>
              props.store.setToggleShowInspectedRailcar(!props.store.toggleShowInspectedRailcar)
            }
            onDelete={
              props.store.toggleShowInspectedRailcar
                ? () => props.store.setToggleShowInspectedRailcar(false)
                : undefined
            }
          />
        )}
      </Stack>

      <RailcarListGrid {...props} isPendingInspection={!props.store.toggleShowInspectedRailcar} />
    </Stack>
  )
})
